import { domQuerySelector } from 'pf-frontend-common/src/helper/dom/query-selector';
import { easingEaseOutSine } from 'pf-frontend-common/src/helper/easing/ease-out-sine';
import { easingLinear } from 'pf-frontend-common/src/helper/easing/linear';
import { BrowserLocationEvent } from 'pf-frontend-common/src/module/browser/location.event';
import { DataKeyValueStringInterface } from 'pf-frontend-common/src/module/data/key-value/string.interface';
import { DomElementEvent } from 'pf-frontend-common/src/module/dom/element.event';
import { StatsContextLocalInterface } from 'pf-frontend-common/src/module/stats/context/local.interface';
import { StatsEntityEnum } from 'pf-frontend-common/src/module/stats/entity.enum';
import { ViewBasicInterface } from 'pf-frontend-common/src/module/view/basic.interface';
import { StatsAttributionServiceInterface } from 'pf-frontend-common/src/service/stats-attribution/service.interface';
import { StatsContexterServiceInterface } from 'pf-frontend-common/src/service/stats-contexter/service.interface';
import { StatsDataServiceInterface } from 'pf-frontend-common/src/service/stats-data/service.interface';
import { WindowServiceInterface } from 'pf-frontend-common/src/service/window/service.interface';
import { WindowLocationServiceInterface } from 'pf-frontend-common/src/service/window-location/service.interface';

import { BrokerModel } from 'common/data/broker/model';
import { BrokerStatsDataAdapter } from 'common/data/broker/stats/data-adapter';
import { LeadStatsLeadContextAdapter } from 'common/data/lead/stats/lead-context-adapter';
import { ContentGroupView } from 'common/module/content-group/view';
import { ContentGroupViewOptionsInterface } from 'common/module/content-group/view-options.interface';
import { DomCallRevealButtonEvent } from 'common/module/dom/call-reveal-button.event';
import { DomCallRevealButtonInterface } from 'common/module/dom/call-reveal-button.interface';
import { LeadEmailForm } from 'common/module/lead/email/form';
import { LeadEmailFormEvent } from 'common/module/lead/email/form.event';
import { LeadEmailFormInputInterface } from 'common/module/lead/email/form/input.interface';
import { StatsTealiumAgentEvent } from 'common/module/stats/tealium/agent.event';
import { StatsTealiumDataLayerEventActionEnum } from 'common/module/stats/tealium/data-layer/event-action.enum';
import { StatsTealiumDataLayerEventLabelEnum } from 'common/module/stats/tealium/data-layer/event-label.enum';
import { StatsTealiumDataLayerTealiumEventEnum } from 'common/module/stats/tealium/data-layer/tealium-event.enum';
import { StatsTealiumDataLayerUserInfoInterface } from 'common/module/stats/tealium/data-layer/user-info.interface';
import { StatsTealiumProviderInterface } from 'common/module/stats/tealium/provider.interface';
import { TextTrimView } from 'common/module/text-trim/view';
import { StatsServiceInterface } from 'common/service/stats/service.interface';
import { BrokerPageViewOptionsInterface } from 'desktop/module/broker/page/view-options.interface';
import { ClientPageViewInterface } from 'desktop/module/client/page/view.interface';

export class BrokerPageView implements ViewBasicInterface, ClientPageViewInterface {
  /**
   * Main HTML element
   */
  private element: HTMLElement;

  /**
   * Tab view html element
   */
  private tabViewEl: HTMLElement;

  /**
   * Broker model
   */
  private brokerModel: BrokerModel;

  /**
   * CSS Selectors
   */
  private cssSelector: DataKeyValueStringInterface = {
    leadPhoneButton: '[data-qs="phone-button"]',
    leadEmailButton: '[data-qs="email-button"]',
    readMore: '[data-qs="text-trimmer"]',
  };

  /**
   * Constructor
   */
  constructor(
    private tabView: ContentGroupView,
    private phoneRevealButtonView: DomCallRevealButtonInterface,
    private textTrimView: TextTrimView,
    private leadEmailForm: LeadEmailForm,
    private statsService: StatsServiceInterface,
    private statsContexterService: StatsContexterServiceInterface,
    private statsDataService: StatsDataServiceInterface,
    private statsAttributionService: StatsAttributionServiceInterface,
    private statsTealium: StatsTealiumProviderInterface,
    private brokerStatsDataAdapter: BrokerStatsDataAdapter,
    private windowService: WindowServiceInterface,
    private windowLocationService: WindowLocationServiceInterface,
    private domGetOffsetTopHelper: (element: HTMLElement) => number
  ) {}

  /**
   * @inheritDoc
   */
  public initialize(options: BrokerPageViewOptionsInterface): void {
    // Keep HTML element
    this.element = options.element;

    // Keep model
    this.brokerModel = options.brokerModel;

    // Initialize stats
    this.initStats();

    // Initialize subviews
    this.initTab(options.subview.tab.viewOptions);
    this.initPhoneRevealButton();
    this.initTextTrim();
    this.initLeadEmailForm();

    // Attach observers
    this.observers();
  }

  /**
   * @inheritDoc
   */
  public render(): void {
    // Log stats
    this.stats();

    // Render subviews
    this.renderTab();
    this.phoneRevealButtonView.render();
    this.textTrimView.render();
  }

  /**
   * Scroll viewport to tab view element
   */
  public scrollToTab(): void {
    this.windowService.scrollToY(this.domGetOffsetTopHelper(this.tabViewEl), 250, easingLinear);
  }

  /**
   * Returns tab view
   */
  public getTabView(): ContentGroupView {
    return this.tabView;
  }

  /**
   * Attach observers
   */
  private observers(): void {
    this.leadEmailForm.getEventEmitter().addListener(LeadEmailFormEvent.submit, this.onSubmitEmail);

    this.phoneRevealButtonView.getEventEmitter().addListener(DomCallRevealButtonEvent.reveal, this.onRevealPhone);

    const leadEmailButton = domQuerySelector(this.element, this.cssSelector.leadEmailButton);

    if (leadEmailButton) {
      leadEmailButton.addEventListener(DomElementEvent.click, this.onClickEmail);
    }

    this.windowLocationService.getEventEmitter().addListener(BrowserLocationEvent.pop, this.onPopBrowserLocation);
  }

  /**
   * Log stats for the page
   */
  private stats(): void {
    // Log stats
    this.statsService.pageView();
    this.statsService.brokerPage(parseInt(this.brokerModel.id, 10));
  }

  /**
   * Inits stats
   */
  private initStats(): void {
    // Set global stats context
    this.statsContexterService.setBrokerPage(true);
    this.statsContexterService.setBrokerId(parseInt(this.brokerModel.id, 10));

    const attributionId = this.statsAttributionService.pop(StatsEntityEnum.broker, this.brokerModel.id);

    if (attributionId) {
      this.statsContexterService.setAuthenticationAttributionId(attributionId);
    }

    // Register stats entities
    this.statsDataService.getBrokerStore().add(this.brokerStatsDataAdapter.getData(this.brokerModel));
  }

  /**
   * Initialize tab view
   */
  private initTab(viewOptions: ContentGroupViewOptionsInterface): void {
    this.tabViewEl = viewOptions.element;
    this.tabView.initialize(viewOptions);
  }

  /**
   * Render the tab content group
   */
  private renderTab(): void {
    this.tabView.render();

    const locationHash = this.windowLocationService.getHash();
    const activeTabs = Object.keys(this.tabView.getActiveSections());

    if (locationHash && this.tabView.registered(locationHash)) {
      this.tabView.change(locationHash);
    } else if (!activeTabs.length) {
      this.tabView.change(this.tabView.getRegistered()[0]);
    }
  }

  /**
   * Initialize the phone reveal button
   */
  private initPhoneRevealButton(): void {
    this.phoneRevealButtonView.initialize({
      element: domQuerySelector(this.element, this.cssSelector.leadPhoneButton),
    });
  }

  /**
   * Initialize the text trimmer component
   */
  private initTextTrim(): void {
    const element = domQuerySelector(this.element, this.cssSelector.readMore);

    this.textTrimView.initialize({
      element,
    });
  }

  /**
   * Initialize lead email form
   */
  private initLeadEmailForm(): void {
    this.leadEmailForm.initialize({
      model: this.brokerModel,
    });
  }

  /**
   * Clicked on a lead phone button
   */
  private onRevealPhone = (): void => {
    const context: StatsContextLocalInterface = {
      lead: {
        medium: 'phone',
        cta: 'button',
      },
    };

    // Log stats
    this.statsService.brokerLeadClick(parseInt(this.brokerModel.id, 10), context);
    this.statsTealium.send(
      StatsTealiumAgentEvent(StatsTealiumDataLayerTealiumEventEnum.callNow, this.brokerModel, {
        isBroker: true,
        eventAction: StatsTealiumDataLayerEventActionEnum.call,
        eventLabel: StatsTealiumDataLayerEventLabelEnum.callDirect,
      })
    );
  };

  /**
   * Clicked email button
   */
  private onClickEmail = (e: Event): void => {
    e.preventDefault();

    const context: StatsContextLocalInterface = {
      lead: {
        medium: 'email',
        cta: 'button',
      },
    };

    // Log stats
    this.statsService.brokerLeadClick(parseInt(this.brokerModel.id, 10), context);

    this.leadEmailForm.initState();
    this.leadEmailForm.render();
  };

  /**
   * Submitted email form
   */
  private onSubmitEmail = (input: LeadEmailFormInputInterface): void => {
    const context: StatsContextLocalInterface = {
      lead: new LeadStatsLeadContextAdapter(input, false).getData(),
    };

    const userInfo: StatsTealiumDataLayerUserInfoInterface = {
      user_full_name: input.name?.value,
      user_email: input.email?.value,
      user_phone_number: input.phone?.value,
    };

    // Log stats
    this.statsService.brokerLeadSend(parseInt(this.brokerModel.id, 10), context);
    this.statsTealium.send(
      StatsTealiumAgentEvent(StatsTealiumDataLayerTealiumEventEnum.emailNow, this.brokerModel, {
        isBroker: true,
        userInfo,
      })
    );
  };

  /**
   * Fired location history pop event
   */
  private onPopBrowserLocation = (): void => {
    const locationHash = this.windowLocationService.getHash();

    if (this.tabView.registered(locationHash) && !this.tabView.getActiveSections()[locationHash]) {
      this.tabView.change(locationHash);

      const button = this.tabView.getButtonElement(locationHash);
      this.windowService.scrollToY(button.offsetTop, 250, easingEaseOutSine);
    }
  };
}
